import { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Button,
  TextArea,
  Checkbox,
  Tag,
  SkeletonText,
  Dropdown,
} from '@carbon/react';
import { MachineLearningModel, Shuffle } from '@carbon/icons-react';
import { Node, Edge } from '@xyflow/react';
import { ConceptNodeData } from './ConceptNode';
import { ontologyGenerationApi } from '../../lib/api/ontology-generation.api';
import type { ExpandedConcept } from '../../lib/api/ontology-generation.api';

/**
 * Common relationship types for ontology expansion
 */
const RELATIONSHIP_TYPES = [
  { id: 'any', label: 'Any relationship (let AI decide)' },
  { id: 'is-a', label: 'Is-a (subtypes / specializations)' },
  { id: 'part-of', label: 'Part-of (components / composition)' },
  { id: 'has-property', label: 'Has-property (attributes)' },
  { id: 'depends-on', label: 'Depends-on (dependencies)' },
  { id: 'uses', label: 'Uses (usage relationships)' },
  { id: 'contains', label: 'Contains (containment)' },
  { id: 'produces', label: 'Produces (outputs / results)' },
  { id: 'extends', label: 'Extends (specialization / extension)' },
  { id: 'implements', label: 'Implements (implementation)' },
  { id: 'relates-to', label: 'Relates-to (general association)' },
  { id: 'interacts-with', label: 'Interacts-with (interaction)' },
  { id: 'causes', label: 'Causes (causation)' },
  { id: 'precedes', label: 'Precedes (temporal ordering)' },
  { id: 'derived-from', label: 'Derived-from (derivation / origin)' },
];

interface ConceptExpanderProps {
  isOpen: boolean;
  onClose: () => void;
  concept: Node | null;
  allNodes: Node[];
  allEdges: Edge[];
  onAddConcepts: (
    concepts: ConceptNodeData[],
    relationships: Array<{ source: string; target: string; label: string }>
  ) => void;
}

/**
 * Generate suggested concepts using the backend AI service
 */
const generateSuggestedConcepts = async (
  concept: ConceptNodeData,
  conceptId: string,
  allNodes: Node[],
  allEdges: Edge[],
  relationshipContext: string = ''
): Promise<ExpandedConcept[]> => {
  // Find linked concepts for context
  const linkedEdges = allEdges.filter(
    (edge) => edge.source === conceptId || edge.target === conceptId
  );
  const linkedNodeIds = linkedEdges.map((edge) =>
    edge.source === conceptId ? edge.target : edge.source
  );
  const linkedNodes = allNodes.filter((node) => linkedNodeIds.includes(node.id));

  // Build context for the AI
  const linkedConcepts = linkedNodes.map((node) => {
    const nodeData = node.data as ConceptNodeData;
    const relationshipEdge = linkedEdges.find(
      (edge) =>
        (edge.source === conceptId && edge.target === node.id) ||
        (edge.target === conceptId && edge.source === node.id)
    );
    const edgeLabel = relationshipEdge?.label;
    return {
      label: nodeData.label,
      definition: nodeData.definition || '',
      relationship: typeof edgeLabel === 'string' ? edgeLabel : 'related_to',
    };
  });

  // Build all nodes context (for avoiding duplicates)
  const allNodesContext = allNodes.map((node) => {
    const nodeData = node.data as ConceptNodeData;
    return {
      id: node.id,
      label: nodeData.label,
      definition: nodeData.definition,
    };
  });

  // Call backend API
  const response = await ontologyGenerationApi.expandConcept({
    concept: {
      id: conceptId,
      label: concept.label,
      definition: concept.definition || '',
      properties: concept.properties?.map((p) =>
        typeof p === 'string' ? p : p.name
      ),
    },
    linkedConcepts,
    allNodes: allNodesContext,
    relationshipContext: relationshipContext || undefined,
    maxSuggestions: 10,
  });

  return response.suggestions;
};

/**
 * ConceptExpander Component
 * 
 * Modal dialog for AI-powered concept expansion.
 * Uses the backend API to generate suggestions via the configured AI provider.
 */
export const ConceptExpander = ({
  isOpen,
  onClose,
  concept,
  allNodes,
  allEdges,
  onAddConcepts,
}: ConceptExpanderProps) => {
  const [selectedConcepts, setSelectedConcepts] = useState<Set<number>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ExpandedConcept[]>([]);
  const [relationshipContext, setRelationshipContext] = useState('');
  const [error, setError] = useState<string | null>(null);

  const conceptData = (concept?.data as ConceptNodeData) || undefined;

  // Load suggestions when dialog opens
  const loadSuggestions = useCallback(async () => {
    if (!concept || !conceptData) return;

    setIsLoading(true);
    setError(null);
    setSelectedConcepts(new Set());

    try {
      const newSuggestions = await generateSuggestedConcepts(
        conceptData,
        concept.id,
        allNodes,
        allEdges,
        relationshipContext
      );
      setSuggestions(newSuggestions);
      
      if (newSuggestions.length === 0) {
        setError('AI returned no suggestions. Try adjusting the relationship context or try again.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate suggestions';
      setError(errorMessage);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [concept, conceptData, allNodes, allEdges, relationshipContext]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen && concept) {
      setSuggestions([]);
      setSelectedConcepts(new Set());
      setRelationshipContext('');
      setError(null);
    }
  }, [isOpen, concept]);

  const handleShuffle = () => {
    loadSuggestions();
  };

  const toggleConcept = (index: number) => {
    const newSelected = new Set(selectedConcepts);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedConcepts(newSelected);
  };

  const handleAddSelected = () => {
    if (!concept) return;
    setIsGenerating(true);

    setTimeout(() => {
      const selectedSuggestions = Array.from(selectedConcepts).map(
        (index) => suggestions[index]
      );
      const newConcepts = selectedSuggestions.map((suggestion) => ({
        label: suggestion.label,
        definition: suggestion.definition,
        properties: suggestion.properties,
      }));

      const relationships = selectedSuggestions.map((suggestion, index) => ({
        source: concept.id,
        target: `concept-${Date.now()}-${index}`,
        label: suggestion.relationship,
      }));

      onAddConcepts(newConcepts, relationships);
      setIsGenerating(false);
      setSelectedConcepts(new Set());
      onClose();
    }, 500);
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading={
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MachineLearningModel size={20} />
          Expand with AI: "{conceptData?.label || ''}"
        </div>
      }
      primaryButtonText={`Add ${selectedConcepts.size} Concept${selectedConcepts.size !== 1 ? 's' : ''}`}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleAddSelected}
      primaryButtonDisabled={selectedConcepts.size === 0 || isGenerating || isLoading}
      size="lg"
    >
      <div style={{ marginBottom: '1rem' }}>
        <p style={{ marginBottom: '1rem', color: 'var(--cds-text-secondary)' }}>
          AI suggests related concepts to expand your ontology. Select the ones you'd like to add.
        </p>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1 }}>
            <Dropdown
              id="relationship-type-dropdown"
              titleText="Relationship type"
              label="Select a relationship type..."
              items={RELATIONSHIP_TYPES}
              itemToString={(item) => item?.label || ''}
              onChange={({ selectedItem }) => {
                if (selectedItem && selectedItem.id !== 'any') {
                  setRelationshipContext(`Focus on "${selectedItem.id}" relationships (${selectedItem.label})`);
                } else {
                  setRelationshipContext('');
                }
              }}
              size="md"
            />
          </div>
        </div>

        <TextArea
          id="relationship-context"
          labelText="Additional context (optional)"
          placeholder="Add more details about what you're looking for (e.g., 'focus on security aspects', 'include implementation patterns'...)"
          value={relationshipContext}
          onChange={(e) => setRelationshipContext(e.target.value)}
          rows={2}
          style={{ marginBottom: '1rem' }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <p style={{ fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
            {isLoading
              ? 'Generating AI suggestions...'
              : suggestions.length > 0
              ? `${suggestions.length} concepts suggested${relationshipContext ? ' (filtered by relationship type)' : ''}`
              : 'Ready to generate AI suggestions'}
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              kind="primary"
              size="sm"
              onClick={loadSuggestions}
              disabled={isLoading}
              renderIcon={MachineLearningModel}
            >
              {isLoading ? 'Generating...' : 'Generate'}
            </Button>
            {suggestions.length > 0 && (
              <Button
                kind="secondary"
                size="sm"
                onClick={handleShuffle}
                disabled={isLoading}
                renderIcon={Shuffle}
              >
                Shuffle
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--cds-support-error)',
              color: 'var(--cds-text-on-color)',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <strong>Error:</strong> {error}
          </div>
        )}

        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {isLoading ? (
            <div style={{ padding: '2rem 0' }}>
              <SkeletonText paragraph lineCount={5} />
            </div>
          ) : suggestions.length === 0 && !error ? (
            <div
              style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'var(--cds-text-secondary)',
              }}
            >
              <MachineLearningModel size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p style={{ fontSize: '0.875rem' }}>
                Click "Generate" to get AI-powered concept suggestions.
              </p>
              <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Optionally describe the relationship type above for targeted results.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  style={{
                    padding: '1rem',
                    border: `2px solid ${
                      selectedConcepts.has(index)
                        ? 'var(--cds-interactive-01)'
                        : 'var(--cds-border-subtle-01)'
                    }`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: selectedConcepts.has(index)
                      ? 'var(--cds-layer-selected-01)'
                      : 'var(--cds-layer-01)',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => toggleConcept(index)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <strong style={{ fontSize: '0.875rem' }}>{suggestion.label}</strong>
                        <Tag type="blue" size="sm">
                          {suggestion.relationship}
                        </Tag>
                      </div>

                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--cds-text-secondary)',
                          marginBottom: '0.75rem',
                        }}
                      >
                        {suggestion.definition}
                      </p>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {suggestion.properties.map((property, propIndex) => (
                          <Tag key={propIndex} type="gray" size="sm">
                            {property}
                          </Tag>
                        ))}
                      </div>
                    </div>

                    <Checkbox
                      id={`concept-${index}`}
                      checked={selectedConcepts.has(index)}
                      onChange={() => toggleConcept(index)}
                      labelText=""
                      hideLabel
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// Made with Bob
